import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import NotificationBell from './NotificationBell';
import NotificationHerald from './NotificationHerald';
import AlertPopup from './AlertPopup';
import {
  HomeIcon,
  UserGroupIcon,
  TruckIcon,
  MapIcon,
  MegaphoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  IdentificationIcon,
  ChartBarIcon,
  BriefcaseIcon,
  UsersIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  DevicePhoneMobileIcon,
  TicketIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  ClockIcon,
  BookOpenIcon,
  SparklesIcon,
  KeyIcon
} from '@heroicons/react/24/outline';

const Layout = () => {
  const { user, logout, isAdmin, isManager } = useAuth();
  const { canView, hasPermission, loading: permissionsLoading } = usePermissions();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout(); // P1-B: revokes the server-side session (POST /api/auth/v2/logout) before leaving
    navigate('/login');
  };

  const navigation = [
    {
      section: 'Main',
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, module: 'dashboard' },
        { name: 'Employees', href: '/employees', icon: UserGroupIcon, module: 'employees' },
        { name: 'My Time Off', href: '/time-off', icon: ClockIcon, module: 'time_off', action: 'create' },
        { name: 'Attendance', href: '/attendance', icon: ClockIcon, module: 'time_off', action: 'approve' },
        // TO1c (R-TO7, ruling 9): the calendar is a VIEW surface — everyone but
        // drivers, supervisors and payroll (who hold no time_off.view) sees it.
        { name: "Who's Out", href: '/whos-out', icon: CalendarDaysIcon, module: 'time_off', action: 'view' },
        { name: 'Fleet', href: '/fleet', icon: TruckIcon, module: 'fleet' },
        { name: 'In-Service', href: '/dispatch', icon: MapIcon, module: 'dispatch' },
        { name: 'Routes', href: '/routes', icon: ArrowsRightLeftIcon, module: 'routes' },
        { name: 'Accidents', href: '/accidents', icon: ExclamationTriangleIcon, module: 'accidents' },
        { name: 'Message Board', href: '/message-board', icon: MegaphoneIcon, module: 'messages' },
        { name: 'Celebrations', href: '/celebrations-board', icon: SparklesIcon, module: 'messages' },
        { name: 'Direct Messages', href: '/direct-messages', icon: EnvelopeIcon, module: 'messages', adminOnly: true },
        { name: 'Calendar', href: '/calendar', icon: CalendarDaysIcon },
      ]
    },
    {
      section: 'Training & Compliance',
      items: [
        { name: 'ABC School', href: '/abc-school', icon: AcademicCapIcon },
        { name: 'Certifications', href: '/certifications', icon: IdentificationIcon, module: 'certifications' },
        { name: 'Handbook Editor', href: '/handbook-editor', icon: BookOpenIcon, adminOnly: true },
      ]
    },
    {
      section: 'Administration',
      items: [
        { name: 'Reports', href: '/reports', icon: ChartBarIcon, module: 'reports', adminOnly: true },
        { name: 'HR', href: '/hr', icon: BriefcaseIcon, module: 'hr_applicants', adminOnly: true },
        { name: 'Payroll', href: '/payroll', icon: BanknotesIcon, module: 'payroll', adminOnly: true },
        { name: 'Website CMS', href: '/website-cms', icon: GlobeAltIcon, module: 'website_cms', superAdminOnly: true },
        { name: 'App Management', href: '/app-management', icon: DevicePhoneMobileIcon, module: 'website_cms', superAdminOnly: true },
        { name: 'Users', href: '/users', icon: UsersIcon, adminOnly: true },
        { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, module: 'settings' },
      ]
    },
    {
      section: 'Accounting',
      items: [
        { name: 'Expenses', href: '/expenses', icon: CurrencyDollarIcon, module: 'expenses' },
        { name: 'Expense Queue', href: '/expenses/admin', icon: CurrencyDollarIcon, module: 'expenses', action: 'manage' },
      ]
    },
    {
      section: 'Support',
      items: [
        { name: 'IT Support', href: '/support', icon: TicketIcon },
        { name: 'Password Vault', href: '/password-vault', icon: KeyIcon, adminOnly: true },
      ]
    }
  ];

  const getUserInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const canAccess = (item) => {
    // Admin always has access to everything
    if (isAdmin) return true;
    
    // Check super admin only items
    if (item.superAdminOnly) return false;
    
    // Check admin only items - manager can also access
    if (item.adminOnly && !isManager) return false;
    
    // Check manager only items
    if (item.managerOnly && !isManager) {
      // Also allow supervisors and payroll
      const allowedRoles = ['manager', 'general_manager', 'supervisor', 'payroll'];
      if (!allowedRoles.includes(user?.role)) return false;
    }
    
    // Check module-based permissions. An optional action key gates on that
    // specific (module, action) instead of view — e.g. the Expense Queue
    // requires expenses:manage, and Attendance requires time_off:approve
    // (TO1a: it is an approver surface, not a viewer one).
    if (item.module && !permissionsLoading) {
      return item.action ? hasPermission(item.module, item.action) : canView(item.module);
    }
    
    return true;
  };

  return (
    <div className="app-container">
      {/* Global Alert Popup for Pages */}
      <AlertPopup />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <NavLink to="/" className="sidebar-logo">
            <img src="/gvc-logo.png" alt="GVC" className="sidebar-logo-image" />
            GVC Portal
          </NavLink>
          <div className="sidebar-subtitle">School Bus Management</div>
        </div>

        <nav className="sidebar-nav">
          {navigation.map((section) => {
            // Filter items based on access
            const accessibleItems = section.items.filter(item => canAccess(item));
            
            // Don't render section if no accessible items
            if (accessibleItems.length === 0) return null;
            
            return (
              <div key={section.section} className="sidebar-section">
                <div className="sidebar-section-title">{section.section}</div>
                {accessibleItems.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `sidebar-link ${isActive ? 'active' : ''}`
                    }
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="sidebar-icon" />
                    {item.name}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name}</div>
              <div className="sidebar-user-role">{user?.role}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="sidebar-logout">
            <ArrowRightOnRectangleIcon className="sidebar-icon" />
            Logout
          </button>
          
          {/* Powered by SVT */}
          <div className="sidebar-powered-by">
            ⚡ Powered by <a href="https://svtconsulting.co" target="_blank" rel="noopener noreferrer">SVT Consulting</a>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="main-content">
        {/* Header */}
        <header className="header">
          <div className="header-left">
            <button 
              className="mobile-menu-button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <Bars3Icon />
            </button>
          </div>
          
          <div className="header-right">
            <NotificationBell />
            
            <div className="header-user">
              <div className="header-user-avatar">
                {getUserInitials(user?.name)}
              </div>
              <span className="header-user-name">{user?.name}</span>
              {user?.role && <span className="header-user-role">{user.role}</span>}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          <Outlet />
        </main>

        {/* E8: mascot notification herald — one global mount, all users */}
        <NotificationHerald />
      </div>
    </div>
  );
};

export default Layout;
